import React from "react";

import { fetchApi } from "../../Network/NetworkService";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";

// Site patient : le lien est destiné aux patients, donc toujours la prod.
const FRONT_URL = "https://feeli.io";

// Générateur de lien de suivi à envoyer au patient sur WhatsApp.
// Pas d'auto-login dans l'URL : côté client, /suivi-dermato et
// /suivi-pathologie redirigent un visiteur non connecté vers
// /login?redirect=... en conservant la query (docteurName, utm_*), puis
// reviennent directement sur le questionnaire.
// Le médecin choisi ici personnalise la question « Souhaitez-vous poursuivre
// la prise en charge avec le médecin X ? » du formulaire, et sa spécialité
// décide du questionnaire visé : dermatologue -> /suivi-dermato, tout le
// reste -> /suivi-pathologie.

// Les specialties des médecins portent la forme servie (« Dermatologie »)
// mais des profils legacy gardent le nom de praticien (« Dermatologue ») —
// on normalise (minuscules, sans accents) et on teste le préfixe commun.
const isDermato = (doctor) =>
  ((doctor && doctor.specialties) || []).some(
    (s) =>
      String(s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .indexOf("dermato") === 0,
  );

class SuiviDermatoLink extends React.Component {
  constructor() {
    super();
    this.state = {
      doctors: [],
      filter: "",
      // "" = pas de médecin choisi -> lien dermato par défaut, et pas de
      // docteurName dans l'URL : le formulaire affiche le libellé générique
      // « le même médecin ».
      selected: "",
      copied: false,
      loading: true,
      // Édition manuelle du lien : null = lien généré, sinon l'URL saisie.
      // Réinitialisé au changement de médecin pour que le lien copié
      // corresponde toujours à la sélection affichée.
      editing: false,
      customUrl: null,
    };
    this.copyTimer = null;
  }

  componentDidMount() {
    fetchApi("/doctors/list")
      .then((res) => {
        this.setState({ doctors: res.doctors || [], loading: false });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération de la liste des médecins",
          "warning",
        );
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
  }

  selectedDoctor = () => {
    const { doctors, selected } = this.state;
    return selected ? doctors.find((d) => d._id === selected) || null : null;
  };

  // Sans médecin choisi, on reste sur la dermato (comportement historique de
  // la page) ; sinon la spécialité décide.
  suiviType = () => {
    const doctor = this.selectedDoctor();
    return !doctor || isDermato(doctor) ? "dermato" : "pathologie";
  };

  buildUrl = () => {
    const doctor = this.selectedDoctor();
    const type = this.suiviType();
    const params = new URLSearchParams();
    if (doctor) {
      params.set("docteurName", doctor.FName + " " + doctor.LName);
    }
    params.set("utm_source", "whatsapp");
    params.set("utm_medium", "whatsapp");
    params.set("utm_campaign", "suivi-" + type + "-whatsapp");
    return FRONT_URL + "/suivi-" + type + "?" + params.toString();
  };

  // URL effectivement copiée : l'override manuel s'il existe, sinon le généré.
  effectiveUrl = () => {
    const { customUrl } = this.state;
    return customUrl !== null ? customUrl : this.buildUrl();
  };

  startEditing = () => {
    this.setState((prev) => ({
      editing: true,
      customUrl: prev.customUrl !== null ? prev.customUrl : this.buildUrl(),
      copied: false,
    }));
  };

  // Fermer l'édition : si le contenu est redevenu identique au lien généré,
  // on abandonne l'override pour que le sélecteur de médecin reprenne la main.
  stopEditing = () => {
    this.setState((prev) => ({
      editing: false,
      customUrl:
        prev.customUrl !== null && prev.customUrl.trim() === this.buildUrl()
          ? null
          : prev.customUrl,
    }));
  };

  resetUrl = () => {
    this.setState({ customUrl: null, editing: false, copied: false });
  };

  markCopied = () => {
    this.setState({ copied: true });
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
    this.copyTimer = setTimeout(() => this.setState({ copied: false }), 2000);
  };

  fallbackCopy = (text) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      this.markCopied();
    } catch (e) {
      notif.addNotification(
        "Erreur",
        "Impossible de copier le lien, sélectionnez-le manuellement",
        "warning",
      );
    }
    document.body.removeChild(ta);
  };

  copyUrl = () => {
    const url = this.effectiveUrl();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(this.markCopied)
        .catch(() => this.fallbackCopy(url));
    } else {
      this.fallbackCopy(url);
    }
  };

  render() {
    const { doctors, filter, selected, copied, loading } = this.state;

    const norm = (s) => (s || "").toLowerCase();
    const visible = filter
      ? doctors.filter(
          (d) => norm(d.FName + " " + d.LName).indexOf(norm(filter)) !== -1,
        )
      : doctors;

    const doctor = this.selectedDoctor();
    const type = this.suiviType();
    const { editing, customUrl } = this.state;
    const url = this.effectiveUrl();
    const isCustomized = customUrl !== null && customUrl.trim() !== this.buildUrl();
    const missingWhatsappUtm =
      isCustomized && url.indexOf("utm_source=whatsapp") === -1;

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">{"Lien suivi — WhatsApp"}</h2>
        </div>

        <div className="dermato-sdr-card">
          <h3 className="dermato-sdr-card-title">{"Générer un lien"}</h3>
          <p style={{ margin: "0 0 16px", color: "#888", fontSize: 14 }}>
            {
              "Choisissez le médecin affiché sur le questionnaire, puis copiez le lien et envoyez-le au patient sur WhatsApp. La spécialité du médecin décide du questionnaire : dermatologue → suivi dermato, autre spécialité → suivi pathologie. À l'ouverture, le patient se connecte et arrive directement sur le suivi (pas d'auto-login dans l'URL)."
            }
          </p>

          <div className="suivi-link-row">
            <div className="suivi-link-field">
              <span className="suivi-link-label">{"Rechercher un médecin"}</span>
              <input
                className="suivi-link-input"
                type="text"
                placeholder="Filtrer par nom…"
                value={filter}
                onChange={(e) => this.setState({ filter: e.target.value })}
              />
            </div>
            <div className="suivi-link-field">
              <span className="suivi-link-label">
                {"Médecin affiché sur le formulaire"}
              </span>
              <select
                className="suivi-link-select"
                value={selected}
                disabled={loading}
                onChange={(e) =>
                  this.setState({
                    selected: e.target.value,
                    copied: false,
                    // Changer de médecin invalide un lien modifié à la main.
                    customUrl: null,
                    editing: false,
                  })
                }
              >
                <option value="">
                  {"— Aucun (libellé générique « le même médecin ») —"}
                </option>
                {visible.map((d) => (
                  <option key={d._id} value={d._id}>
                    {"Dr " +
                      d.FName +
                      " " +
                      d.LName +
                      (d.specialties && d.specialties.length
                        ? " — " + d.specialties.join(", ")
                        : "")}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="suivi-link-url-box">
            {editing ? (
              <textarea
                className="suivi-link-edit"
                value={customUrl !== null ? customUrl : url}
                onChange={(e) =>
                  this.setState({ customUrl: e.target.value, copied: false })
                }
                spellCheck={false}
              />
            ) : (
              <span className="suivi-link-url">{url}</span>
            )}
            <button
              className="dermato-sdr-btn dermatosdr-btn-primary"
              onClick={this.copyUrl}
            >
              {copied ? "Copié ✓" : "Copier le lien"}
            </button>
            {editing ? (
              <button
                className="dermato-sdr-btn dermatosdr-btn-link"
                onClick={this.stopEditing}
              >
                {"Terminer"}
              </button>
            ) : (
              <button
                className="dermato-sdr-btn dermatosdr-btn-link"
                onClick={this.startEditing}
              >
                {"Modifier le lien"}
              </button>
            )}
            {isCustomized && (
              <button
                className="dermato-sdr-btn dermatosdr-btn-link"
                onClick={this.resetUrl}
              >
                {"Rétablir le lien généré"}
              </button>
            )}
          </div>

          {(editing || isCustomized) && (
            <div className="suivi-link-warning">
              <i className="zmdi zmdi-alert-triangle" />
              <div>
                <strong>
                  {"Lien modifié à la main — la sélection de médecin ci-dessus ne s'applique plus."}
                </strong>
                <ul>
                  <li>
                    {"Gardez "}
                    <code>{"utm_source=whatsapp"}</code>
                    {" : sans lui, le suivi ne sera pas compté « Via lien WhatsApp » dans les pages Suivis et le tracking."}
                  </li>
                  <li>
                    {"Gardez le bon questionnaire : "}
                    <code>{"/suivi-dermato"}</code>
                    {" pour un dermatologue, "}
                    <code>{"/suivi-pathologie"}</code>
                    {" pour les autres spécialités."}
                  </li>
                  <li>
                    {"Dans "}
                    <code>{"docteurName"}</code>
                    {", remplacez les espaces par "}
                    <code>{"+"}</code>
                    {" (encodage URL)."}
                  </li>
                </ul>
                {missingWhatsappUtm && (
                  <p style={{ margin: "8px 0 0", color: "#b8323f", fontWeight: 600 }}>
                    {"⚠ Ce lien ne contient plus utm_source=whatsapp : il ne sera pas compté comme un suivi WhatsApp."}
                  </p>
                )}
              </div>
            </div>
          )}

          {isCustomized ? (
            <p className="suivi-link-hint">
              {"Lien : "}
              <strong>{"personnalisé"}</strong>
              {" (saisi à la main, voir l'avertissement ci-dessus)"}
            </p>
          ) : (
            <p className="suivi-link-hint">
              {"Lien généré : "}
              <strong>
                {type === "dermato" ? "suivi dermato" : "suivi pathologie"}
              </strong>
              {doctor
                ? " (spécialité : " +
                  ((doctor.specialties || []).join(", ") || "non renseignée") +
                  ")"
                : " (par défaut, aucun médecin choisi)"}
            </p>
          )}

          {doctor ? (
            <p className="suivi-link-hint">
              {"Le formulaire demandera : « Souhaitez-vous poursuivre la prise en charge avec le médecin "}
              <strong>{doctor.FName + " " + doctor.LName}</strong>
              {" ? »"}
            </p>
          ) : (
            <p className="suivi-link-hint">
              {
                "Sans médecin choisi, le formulaire affiche « … poursuivre la prise en charge avec le même médecin ? »"
              }
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default SuiviDermatoLink;
